import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { GridValidRowModel } from "@mui/x-data-grid-premium";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintMonthYear } from "util/dates";

import { ExpandableInfoContainerCombined } from "./ExpandableInfoSection";
import { useGeneratorStatusSummary } from "./useGeneratorStatusSummary";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { useWasteFacility } from "./useWasteFacility";
import { useWasteFacilityState } from "./WasteFacilityContext";

const updateNotificationTooltip =
  "If the facility's calculated generator statuses differ from the EPA current status, you may need to file an update notification with the EPA.";

const currentStatusTooltip =
  "Federal waste generator status as currently listed with the EPA, for both federal and state, if applicable.\
  \n\nReported data can take up to 2 weeks to update.\n\n" + updateNotificationTooltip;

const epaStatusChangesTooltip =
  "The generator statuses listed with the EPA, if they change month to month.";

const calculatedStatusesTooltip =
  "Statuses are calculated from vendor line items, but if no vendor data is present, we will use e‑Manifest data.";

const potentialExceedancesTooltip =
  "Any months where our calculated generator status exceeds the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const belowThresholdTooltip =
  "Any months where our calculated generator status is below the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const prettyPrintWasteGeneratorStatusLong = ({
  status,
}: {
  status: RcraInfoFederalWasteGeneratorStatus | StateRegulatoryStatus;
}) => {
  switch (status) {
    case RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator:
      return "Very Small Quantity Generator (VSQG)";
    case RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator:
      return "Small Quantity Generator (SQG)";
    case RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator:
      return "Large Quantity Generator (LQG)";
    case RcraInfoFederalWasteGeneratorStatus.NotAGenerator:
      return "Not a Generator";
    case RcraInfoFederalWasteGeneratorStatus.Undetermined:
      return "Undetermined";
    default:
      return prettyPrintEnumValue(status);
  }
};

export const GeneratorStatusTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 5 }}>
      <GeneratorStatusTileContent />
    </DashboardTile>
  );
};

const GeneratorStatusTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: wasteFacilityData } = useWasteFacility(facilityId);
  const {
    wasteFacilityState: { optimisticFilters, deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const { data: generatorStatusData } = useGeneratorStatusSummary(
    effectiveWasteFacilityId,
    deferredFilters.startDate ? new Date(deferredFilters.startDate) : null,
    deferredFilters.endDate ? new Date(deferredFilters.endDate) : null
  );

  const hasStartAndEndDate = Boolean(
    (optimisticFilters.startDate && optimisticFilters.endDate) ||
    (deferredFilters.startDate && deferredFilters.endDate)
  );

  const { currentlyReportedStatus, statusChanges, exceedances, belowThreshold } =
    generatorStatusData?.generatorStatusSummary ?? {};

  const currentlyReportedStatusArray = useMemo(() => {
    const res = [];
    if (currentlyReportedStatus?.fedWasteGenerator) {
      res.push({
        status: currentlyReportedStatus.fedWasteGenerator.status,
        date: currentlyReportedStatus.fedWasteGenerator.createdDate,
        type: "Federal",
      });
    }
    if (
      currentlyReportedStatus?.stateWasteGenerator &&
      stateDoesNotFollowFederalGeneratorStatus(wasteFacilityData?.facility?.state)
    ) {
      res.push({
        status: currentlyReportedStatus.stateWasteGenerator.status,
        date: currentlyReportedStatus.stateWasteGenerator.createdDate,
        type: `${wasteFacilityData?.facility?.state} State`,
      });
    }
    return res;
  }, [currentlyReportedStatus, wasteFacilityData]);

  // Check if there's any shipment-based data to determine if we should show calculated sections
  const hasShipmentData = useHasWasteShipmentData();
  const theme = useTheme();

  const isLoading = !hasStartAndEndDate || !generatorStatusData;

  return (
    <>
      <DashboardTile.Header title="Generator Status" />
      <DashboardTile.ContentArea>
        <Stack spacing={2}>
          {isLoading && (
            <Stack gap={2}>
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
            </Stack>
          )}
          {/* Currently Reported Status - Not expandable */}
          {generatorStatusData && (
            <Stack
              sx={{
                backgroundColor: theme.palette.grey50.main,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                gap: 0.25,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.25}>
                <Typography fontSize={11} color="textSecondary">
                  {currentlyReportedStatusArray.length > 0
                    ? `EPA Current Status as of ${prettyPrintMonthYear(
                        currentlyReportedStatusArray[0].date
                      )}`
                    : "Facility does not have a generator status listed with the EPA"}
                </Typography>
                <InfoIconTooltip
                  tooltip={currentStatusTooltip}
                  iconButtonSx={{ color: theme.palette.grey[400] }}
                />
              </Stack>
              <Stack gap={0.25}>
                {currentlyReportedStatusArray.map((item) => (
                  <Stack direction="row" spacing={1} key={item.type}>
                    <Typography variant="body2" fontSize={"0.875rem"}>
                      {item.status &&
                        prettyPrintWasteGeneratorStatusLong({
                          status: item.status,
                        })}
                    </Typography>
                    <Typography
                      fontStyle="italic"
                      color="text.disabled"
                      variant="caption"
                      fontSize={"0.75rem"}
                    >
                      {item.type}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}

          {/* Only show these sections if we have shipment data */}
          {hasShipmentData && (
            <>
              {/* Reported Status Changes - Expandable */}
              {statusChanges && statusChanges.changes.length > 0 && (
                <ExpandableInfoContainerCombined
                  label="EPA Status Changes"
                  text={`${statusChanges.changeCount} ${pluralize(
                    "change",
                    statusChanges.changeCount
                  )} this period`}
                  disabled={!statusChanges.changes.length}
                  infoTooltip={epaStatusChangesTooltip}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "Federal Status",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "reportedStateGeneratorStatus",
                        headerName: "State Status",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={statusChanges.changes}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* Potential Exceedances - Expandable */}
              {exceedances && exceedances?.length > 0 && (
                <ExpandableInfoContainerCombined
                  showWarningChip={!!(exceedances?.length && exceedances.length > 0)}
                  disabled={!exceedances?.length}
                  label="Potential Exceedances"
                  infoTooltip={potentialExceedancesTooltip}
                  text={`${exceedances?.length ?? 0} potential ${pluralize(
                    "exceedance",
                    exceedances?.length ?? 0
                  )} this period`}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedFederalGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={exceedances}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* Below threshold - Expandable */}
              {belowThreshold && belowThreshold?.length > 0 && (
                <ExpandableInfoContainerCombined
                  disabled={!belowThreshold?.length}
                  label="Below Threshold"
                  infoTooltip={belowThresholdTooltip}
                  text={`${belowThreshold?.length ?? 0} ${pluralize(
                    "month",
                    belowThreshold?.length ?? 0
                  )} this period`}
                >
                  <GreySimpleGrid
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedFederalGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    getRowId={(row) => row.month}
                    rows={belowThreshold}
                  />
                </ExpandableInfoContainerCombined>
              )}
            </>
          )}
        </Stack>
      </DashboardTile.ContentArea>
    </>
  );
};

function GreySimpleGrid<T extends GridValidRowModel>(props: DataGridProps<T>) {
  const theme = useTheme();
  return (
    <DataGrid
      sx={{
        border: "0px",
        backgroundColor: "transparent",
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: theme.palette.grey50.main,
          p: 1,
        },
        "& .MuiDataGrid-cell": {
          px: 1,
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
      }}
      disableColumnMenu
      disableColumnReorder
      disableColumnSorting
      disableColumnResize
      {...props}
    />
  );
}
